import { useEffect, useState, useRef, useCallback, useSyncExternalStore } from 'react';
import { getApiLog, clearApiLog, subscribeApiLog, type ApiLogEntry } from '../../lib/api-client';
import { BoltIcon, XMarkIcon } from '@heroicons/react/24/outline';

const MIN_HEIGHT = 120;
const MAX_HEIGHT = 600;
const DEFAULT_HEIGHT = 260;

/**
 * Debug Footer — shows all API calls in a resizable panel.
 * Toggle: Cmd + Shift + L (Mac)
 * Drag top edge to resize.
 */
export function DebugFooter() {
  const [visible, setVisible] = useState(false);
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const dragging = useRef(false);
  const startY = useRef(0);
  const startH = useRef(DEFAULT_HEIGHT);

  const log = useSyncExternalStore(subscribeApiLog, getApiLog);

  // Keyboard shortcut: Cmd+Shift+L
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.metaKey && e.shiftKey && e.key.toLowerCase() === 'l') {
        e.preventDefault();
        setVisible(v => !v);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Drag resize handlers
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    startY.current = e.clientY;
    startH.current = height;
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  }, [height]);

  useEffect(() => {
    function onMouseMove(e: MouseEvent) {
      if (!dragging.current) return;
      const delta = startY.current - e.clientY;
      setHeight(Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, startH.current + delta)));
    }
    function onMouseUp() {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        height: `${height}px`,
        background: '#1a1a2e',
        borderTop: '2px solid #e94560',
        color: '#eee',
        fontFamily: "Arial",
        fontSize: '11px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Drag Handle */}
      <div
        onMouseDown={onMouseDown}
        style={{
          height: '6px',
          cursor: 'ns-resize',
          background: 'transparent',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <div style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '36px',
          height: '3px',
          borderRadius: '2px',
          background: 'rgba(255,255,255,0.15)',
        }} />
      </div>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '4px 12px',
          background: '#16213e',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#e94560', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}><BoltIcon style={{ width: 14 }} /> API Debug</span>
          <span style={{ color: '#666', fontSize: '10px' }}>{log.length} calls</span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={clearApiLog}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#aaa',
              padding: '2px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '10px',
            }}
          >
            Clear
          </button>
          <button
            onClick={() => setVisible(false)}
            style={{
              background: 'rgba(233,69,96,0.15)',
              border: '1px solid rgba(233,69,96,0.3)',
              color: '#e94560',
              padding: '2px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '10px',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><XMarkIcon style={{ width: 13 }} /> Close</span>
          </button>
        </div>
      </div>

      {/* Log Table */}
      <div style={{ overflow: 'auto', flex: 1 }}>
        {log.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#555' }}>
            No API calls yet. Navigate to trigger requests.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.03)', position: 'sticky', top: 0 }}>
                {['Method', 'Path', 'Status', 'Duration', 'Size', 'Time'].map(h => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: '4px 8px',
                      color: '#666',
                      fontWeight: 600,
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                      background: '#1a1a2e',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {log.map(entry => (
                <LogRow key={entry.id} entry={entry} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Shortcut hint */}
      <div style={{ padding: '3px 12px', background: '#16213e', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
        <span style={{ color: '#444', fontSize: '9px' }}>Drag top edge to resize</span>
        <span style={{ color: '#444', fontSize: '9px' }}>⌘ + ⇧ + L to toggle</span>
      </div>
    </div>
  );
}

function LogRow({ entry }: { entry: ApiLogEntry }) {
  const isError = entry.status === 0 || (entry.status && entry.status >= 400);
  const methodColors: Record<string, string> = {
    GET: '#53d769',
    POST: '#5ac8fa',
    PUT: '#ffcc00',
    PATCH: '#ff9500',
    DELETE: '#ff3b30',
  };

  return (
    <tr
      style={{
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        background: isError ? 'rgba(233,69,96,0.08)' : 'transparent',
      }}
      title={entry.error || undefined}
    >
      <td style={{ padding: '3px 8px' }}>
        <span style={{ color: methodColors[entry.method] || '#aaa', fontWeight: 700, fontSize: '10px' }}>
          {entry.method}
        </span>
      </td>
      <td style={{ padding: '3px 8px', color: isError ? '#e94560' : '#ccc', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {entry.path}
      </td>
      <td style={{ padding: '3px 8px' }}>
        <span style={{ color: statusColor(entry.status), fontWeight: 600 }}>{entry.status || '—'}</span>
      </td>
      <td style={{ padding: '3px 8px', color: durationColor(entry.duration) }}>{entry.duration}ms</td>
      <td style={{ padding: '3px 8px', color: '#777' }}>{entry.responseSize != null ? formatBytes(entry.responseSize) : '—'}</td>
      <td style={{ padding: '3px 8px', color: '#555' }}>{new Date(entry.timestamp).toLocaleTimeString('vi-VN', { hour12: false })}</td>
    </tr>
  );
}

function statusColor(s: number | null): string {
  if (!s || s === 0) return '#e94560';
  if (s < 300) return '#53d769';
  if (s < 400) return '#ffcc00';
  return '#e94560';
}

function durationColor(ms: number): string {
  if (ms < 200) return '#53d769';
  if (ms < 500) return '#ffcc00';
  if (ms < 1000) return '#ff9500';
  return '#e94560';
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)}KB`;
  return `${(b / (1024 * 1024)).toFixed(1)}MB`;
}
